import { org_sector_type_enum } from '@predium/enums';
import { HasRequiredKeys } from 'type-fest';
import en from './../locales/en.json';

enum count_suffix_enum {
  ONE = 'one',
  OTHER = 'other',
}

type UnProcessedTranslations = typeof en;

/**
 * We check if the key has _one or _other and strip that off the key.
 *
 * This is useful to prevent the use of KEY_one or KEY_other in the code directly.
 */
type CountSuffixes = `_${count_suffix_enum}`;
type ContextSuffixes = `_${org_sector_type_enum}` | `_${org_sector_type_enum}${CountSuffixes}`;

type StripKeyFromSuffix<Key, Suffix extends string> = Key extends `${infer P}${Suffix}` ? P : Key;

type StripKeyFromCount<Key> = StripKeyFromSuffix<Key, CountSuffixes>;
type StripKeyFromContext<Key> = StripKeyFromSuffix<Key, ContextSuffixes>;

type StripKey<Key> = StripKeyFromContext<StripKeyFromCount<Key>>;

type RequiredKeysByKey<Key> = (StripKeyFromContext<Key> extends Key ? {} : { orgSector: org_sector_type_enum }) &
  (StripKeyFromCount<Key> extends Key ? {} : { count: number });

/**
 * Here we generate a typing as typescript doesn't generate the correct typing from json files.
 *
 * For example, we get:
 *
 * ```typescript
 * type UnProcessedTranslations = {
 *  Key1: string;
 *  Key2: string;
 *  Key3_one: string;
 *  Key3_other: string;
 * }
 * ```
 *
 * we turn them into:
 *
 * ```typescript
 * type ProcessedTranslations = {
 *  Key1: {};
 *  Key2: {};
 *  Key3: { count: number };
 * }
 * ```
 */
export type ProcessedTranslations = {
  [Key in keyof UnProcessedTranslations as StripKey<Key>]: RequiredKeysByKey<Key>;
};

export type I18nKeys = keyof ProcessedTranslations;

export type ValuesType = Record<string, string | number | undefined>;

/**
 * We check if there are required keys in the translation, then we require passing them.
 */
export type PreTFunctionArgs<Key extends I18nKeys> =
  HasRequiredKeys<ProcessedTranslations[Key]> extends true
    ? [Key, ProcessedTranslations[Key] & ValuesType]
    : [Key] | [Key, ProcessedTranslations[Key] & ValuesType];

export type PreTFunction = <Key extends I18nKeys>(...args: PreTFunctionArgs<Key>) => string;

void [
  [
    'General_EconomicUnit',
    { orgSector: org_sector_type_enum.BANKING },
  ] satisfies PreTFunctionArgs<'General_EconomicUnit'>,
  ['DataCollection_EnergySupplier', { count: 2 }] satisfies PreTFunctionArgs<'DataCollection_EnergySupplier'>,
  [
    'General_DeleteModalConfirmation-EconomicUnit',
    { count: 2, orgSector: org_sector_type_enum.BANKING },
  ] satisfies PreTFunctionArgs<'General_DeleteModalConfirmation-EconomicUnit'>,
  [
    'General_DeleteModalTitle-ReportAnswer',
    { random: 'random' },
  ] satisfies PreTFunctionArgs<'General_DeleteModalTitle-ReportAnswer'>,
  [
    'General_DeleteModalTitle-EconomicUnit',
    { count: 2, orgSector: org_sector_type_enum.BANKING, name: 'name' },
  ] satisfies PreTFunctionArgs<'General_DeleteModalTitle-EconomicUnit'>,
  [
    'General_EconomicUnitSelected',
    // @ts-expect-error - we don't have a orgSector suffix for this key
    { count: 2 },
  ] satisfies PreTFunctionArgs<'General_EconomicUnitSelected'>,
  [
    'General_EconomicUnitSelected',
    // @ts-expect-error - we don't have a orgSector suffix for this key
    { orgSector: org_sector_type_enum.BANKING },
  ] satisfies PreTFunctionArgs<'General_EconomicUnitSelected'>,

  'Key' satisfies StripKeyFromCount<'Key_one'>,
  'Key' satisfies StripKey<'Key_one'>,
  'Key' satisfies StripKeyFromContext<'Key_BANKING_one'>,
  'Key' satisfies StripKeyFromContext<'Key_BANKING'>,

  // @ts-expect-error - we don't have a orgSector suffix for this key
  'Key_BANKING' satisfies StripKey<'Key_BANKING_one'>,
  // @ts-expect-error - we don't have a orgSector suffix for this key
  'Key_BANKING' satisfies StripKey<'Key_BANKING'>,
  // @ts-expect-error - we don't have a orgSector suffix for this key
  'Key_BANKING_one' satisfies StripKey<'Key_BANKING_one'>,
  // @ts-expect-error - we don't have a orgSector suffix for this key
  'Key_BANKING' satisfies StripKey<'Key_BANKING'>,
  // @ts-expect-error - we don't have a orgSector suffix for this key
  'Key_BANKING_one' satisfies StripKey<'Key_BANKING_one'>,
];
